<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'Register',
})
</script>
<template>
  <div class="page">
    <NavBar :title="lang('register_title')" fixed>
      <template #right>
        <div class="lang-switch" @click="showLangPicker = true">
          <img class="lang-icon" :src="currentLangOption.icon" alt="" />
          <span class="lang-label">{{ currentLangOption.label }}</span>
        </div>
      </template>
    </NavBar>

    <!-- 语言切换下拉 -->
    <VanPopup
      v-model:show="showLangPicker"
      position="top"
      :style="{ top: `calc(56px + ${safeAreaTop})` }"
      class="lang-popup"
    >
      <div class="lang-list">
        <div
          v-for="item in LANG_OPTIONS"
          :key="item.value"
          class="lang-item"
          :class="{ active: userInfo.langType === item.value }"
          @click="switchLang(item.value)"
        >
          <img class="lang-item-icon" :src="item.icon" alt="" />
          <span class="lang-item-label">{{ item.label }}</span>
        </div>
      </div>
    </VanPopup>
    <div class="container">
      <VanForm ref="formRef" @submit="onSubmit">
        <!-- 账号信息 -->
        <div class="section">
          <div class="section-title">{{ lang('account_info') }}</div>
          <VanCellGroup inset>
            <!-- 手机号 -->
            <VanField
              v-model="form.phone"
              :placeholder="lang('phone_placeholder')"
              type="tel"
              maxlength="11"
              :rules="[
                { required: true, message: lang('phone_required') },
                {
                  validator: validatePhoneLength,
                  message: lang('phone_length')
                }
              ]"
              @update:model-value="onPhoneChange"
            />

            <!-- 登录密码 -->
            <VanField
              v-model="form.password"
              :placeholder="lang('password_placeholder')"
              :type="showPassword ? 'text' : 'password'"
              :rules="[{ required: true, message: lang('password_required') }]"
            >
              <template #right-icon>
                <VanIcon
                  :name="showPassword ? 'eye-o' : 'closed-eye'"
                  size="20"
                  color="#999999"
                  @click="showPassword = !showPassword"
                />
              </template>
            </VanField>

            <!-- 验证码 -->
            <VanField
              v-model="form.verificationCode"
              :placeholder="lang('verification_code_placeholder')"
              type="digit"
              maxlength="6"
              :rules="[
                { required: true, message: lang('verification_code_required') },
                {
                  validator: validateCodeFormat,
                  message: lang('verification_code_format')
                }
              ]"
              @update:model-value="onCodeChange"
            >
              <template #button>
                <span
                  class="send-code-btn"
                  :class="{ disabled: isCountingDown || isSendingCode }"
                  @click="onSendCode"
                >
                  {{ countdownText }}
                </span>
              </template>
            </VanField>

            <!-- 隐私协议 -->
            <div class="privacy-wrap">
              <VanCheckbox
                v-model="form.agreePrivacy"
                shape="square"
                icon-size="14px"
                checked-color="#e60012"
              >
                <span class="privacy-text">
                  {{ lang('privacy_prefix') }}
                  <span class="privacy-link" @click.stop="onPrivacyClick">
                    {{ lang('privacy_terms') }}
                  </span>
                </span>
              </VanCheckbox>
            </div>

            <!-- 下载应用 -->
            <div class="download-wrap">
              <span class="has-account">{{
                lang('already_have_account')
              }}</span>
              <a class="download-link" :href="DOWNLOAD_URL" target="_blank">
                {{ lang('download_jt_malaysia_app') }}
              </a>
            </div>
          </VanCellGroup>
        </div>

        <!-- 身份信息 -->
        <div class="section">
          <div class="section-title">{{ lang('identity_info') }}</div>
          <VanCellGroup inset>
            <!-- 姓名 -->
            <VanField
              v-model="form.name"
              :placeholder="lang('name_placeholder')"
              maxlength="50"
              :rules="[
                { validator: validateName, message: lang('name_length') },
                {
                  validator: validateNoInvalidChars,
                  message: lang('name_invalid_char')
                }
              ]"
            />

            <!-- 身份证号/护照号/SSM -->
            <VanField
              v-model="form.idNumber"
              :placeholder="lang('id_number_placeholder')"
              type="digit"
              maxlength="12"
              :rules="[
                { required: true, message: lang('id_number_required') },
                {
                  validator: validateIdNumberFormat,
                  message: lang('id_number_format')
                }
              ]"
            />

            <!-- 邮箱 -->
            <VanField
              v-model="form.email"
              :placeholder="lang('email_placeholder')"
              type="email"
              :rules="[
                { required: true, message: lang('email_required') },
                {
                  validator: validateEmailFormat,
                  message: lang('email_format')
                }
              ]"
            />

            <!-- 推荐码 -->
            <VanField
              v-model="form.referralCode"
              :placeholder="lang('referral_code_placeholder')"
              readonly
              class="readonly-field"
            />
          </VanCellGroup>
        </div>

        <!-- 联系地址 -->
        <div class="section">
          <div class="section-title">{{ lang('contact_address') }}</div>
          <VanCellGroup inset>
            <!-- 邮编 -->
            <VanField
              v-model="form.postcode"
              :placeholder="lang('postcode_placeholder')"
              type="digit"
              maxlength="5"
              :rules="[
                {
                  validator: validatePostcode,
                  message: lang('postcode_format')
                }
              ]"
              @update:model-value="onPostcodeChange"
            />
            <div v-if="postcodeError" class="postcode-error">
              {{ lang('postcode_not_found') }}
            </div>

            <!-- 省市区 -->
            <VanField
              v-model="areaText"
              :placeholder="lang('area_placeholder')"
              readonly
              class="readonly-field"
              :rules="[
                { validator: validateCity, message: lang('city_required') }
              ]"
            />

            <!-- 地址 -->
            <VanField
              v-model="form.address"
              :placeholder="lang('address_placeholder')"
              type="textarea"
              rows="2"
              maxlength="250"
              show-word-limit
              :rules="[
                {
                  validator: validateAddress,
                  message: lang('address_length')
                },
                {
                  validator: validateNoInvalidChars,
                  message: lang('address_invalid_char')
                }
              ]"
            />
          </VanCellGroup>
        </div>

        <!-- 完成注册 -->
        <footer class="submit-wrap">
          <VanButton
            block
            type="primary"
            native-type="submit"
            :loading="isSubmitting"
            :disabled="isSubmitting || !isFormValid"
          >
            {{ lang('complete_register') }}
          </VanButton>
        </footer>
      </VanForm>
    </div>
  </div>
  <div id="cap_iframe"></div>
</template>

<script setup lang="ts" name="Register">
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  Form as VanForm,
  Field as VanField,
  CellGroup as VanCellGroup,
  Checkbox as VanCheckbox,
  Button as VanButton,
  Icon as VanIcon,
  Popup as VanPopup,
  Locale
} from 'vant'
// @ts-ignore
import enUS from 'vant/es/locale/lang/en-US.mjs'
// @ts-ignore
import zhCN from 'vant/es/locale/lang/zh-CN.mjs'
import { showFailToast } from 'vant'
import NavBar from '@/components/NavBar.vue'
import lang from '@/langs'
import {
  sendRegisterCode,
  checkPhoneRegister,
  verifyPhoneCode,
  findProCityArea,
  submitRegister
} from '@/api/requests/register'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import {
  createTencentCaptcha,
  type TencentCaptchaInstance,
  type TencentCaptchaResponse
} from '@/utils/tencentCaptcha'
import langZhIcon from '@/assets/images/lang/lang-zh.png'
import langEnIcon from '@/assets/images/lang/lang-en.png'
import langMsIcon from '@/assets/images/lang/lang-ms.png'

const DOWNLOAD_URL = 'https://download.jtexpress.my/'
const COUNTDOWN_SECONDS = 60

const LANG_OPTIONS = [
  {
    value: 'zh' as const,
    label: '中文',
    icon: langZhIcon
  },
  {
    value: 'en' as const,
    label: 'English',
    icon: langEnIcon
  },
  {
    value: 'ms' as const,
    label: 'Bahasa Melayu',
    icon: langMsIcon
  }
]

const showLangPicker = ref(false)

const currentLangOption = computed(() => {
  return (
    LANG_OPTIONS.find((item) => item.value === userInfo.value.langType) ||
    LANG_OPTIONS[1]
  )
})

const switchLang = (langType: 'zh' | 'en' | 'ms') => {
  setUserInfo({ ...userInfo.value, langType })
  if (langType === 'zh') {
    Locale.use('zh-CN', zhCN)
  } else {
    Locale.use('en-US', enUS)
  }
  showLangPicker.value = false
}
const INVALID_CHAR_REGEX = /[+$%=<>!]/
interface RegisterForm {
  phone: string
  password: string
  verificationCode: string
  name: string
  idNumber: string
  email: string
  referralCode: string
  postcode: string
  province: string
  city: string
  area: string
  address: string
  agreePrivacy: boolean
  verifyToken: string
}

interface AreaNode {
  Name?: string
  proname?: string
  cityname?: string
  areaname?: string
  postcode?: string
  ChildNodes?: AreaNode[]
}

interface AreaMatch {
  province: string
  city: string
  area: string
}

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)
const { setUserInfo } = userStore
const { safeArea } = userInfo.value
const safeAreaTop = `${safeArea.top}px`
const safeAreaBottom = `${safeArea.bottom}px`
const formRef = ref<InstanceType<typeof VanForm>>()

const form = reactive<RegisterForm>({
  phone: '',
  password: '',
  verificationCode: '',
  name: '',
  idNumber: '',
  email: '',
  referralCode: '',
  postcode: '',
  province: '',
  city: '',
  area: '',
  address: '',
  agreePrivacy: false,
  verifyToken: ''
})

const showPassword = ref(false)
const postcodeError = ref(false)
const countdown = ref(0)
const isSubmitting = ref(false)
const isSendingCode = ref(false)
const proCityAreas = ref<AreaNode[]>([])
const postcodeAreaMap = ref<Map<string, AreaMatch>>(new Map())
let countdownTimer: ReturnType<typeof setInterval> | null = null
let captcha: TencentCaptchaInstance | null = null
let captchaInitPromise: Promise<void> | null = null
let isUnmounted = false

const isCountingDown = computed(() => countdown.value > 0)
const countdownText = computed(() => {
  return isCountingDown.value ? `${countdown.value}s` : lang('send_code')
})
const areaText = computed(() => {
  return [form.province, form.city, form.area].filter(Boolean).join('/')
})

// 表单基础校验（必填字段是否已填，不含隐私协议勾选）
const isFormValid = computed(() => {
  return (
    form.phone.trim() !== '' &&
    form.password.trim() !== '' &&
    form.verificationCode.trim() !== '' &&
    form.idNumber.trim() !== '' &&
    form.email.trim() !== ''
  )
})

const buildPostcodeAreaMap = (areas: AreaNode[]) => {
  const nextMap = new Map<string, AreaMatch>()

  areas.forEach((provinceNode) => {
    const province = provinceNode.Name || provinceNode.proname || ''
    provinceNode.ChildNodes?.forEach((cityNode) => {
      const city = cityNode.Name || cityNode.cityname || ''
      cityNode.ChildNodes?.forEach((areaNode) => {
        const area = areaNode.Name || areaNode.areaname || ''
        const postcodes = (areaNode.postcode || '')
          .split(',')
          .map((postcode) => postcode.trim())
          .filter(Boolean)

        postcodes.forEach((postcode) => {
          nextMap.set(postcode, { province, city, area })
        })
      })
    })
  })

  postcodeAreaMap.value = nextMap
}

const fetchProCityArea = async () => {
  try {
    const res: any = await findProCityArea()
    const areas = Array.isArray(res?.ProCityAreas) ? res.ProCityAreas : []
    proCityAreas.value = areas
    buildPostcodeAreaMap(areas)
  } catch (error) {
    console.error('Fetch pro city area failed:', error)
  }
}

const getCurrentLangType = (): 'zh' | 'en' | 'ms' => {
  const queryLang = route.query.lang as string | undefined
  const langType = queryLang === 'my' ? 'ms' : queryLang

  if (langType === 'zh' || langType === 'en' || langType === 'ms') {
    return langType
  }

  return userInfo.value.langType || 'en'
}

const setLangByQuery = () => {
  const langType = getCurrentLangType()
  setUserInfo({
    ...userInfo.value,
    langType
  })
}

const initTencentCaptcha = async () => {
  if (captcha) return
  if (captchaInitPromise) return captchaInitPromise

  captchaInitPromise = (async () => {
    const captchaContainer = document.getElementById('cap_iframe')
    if (!captchaContainer) {
      throw new Error('Tencent captcha container not found')
    }

    captcha = await createTencentCaptcha({
      container: captchaContainer,
      callback: tencentCaptchaCallback,
      langType: getCurrentLangType()
    })
  })()

  try {
    await captchaInitPromise
  } finally {
    captchaInitPromise = null
  }
}

// 推荐码从 URL query 读取
onMounted(() => {
  setLangByQuery()
  const referralCode = route.query.referralCode as string
  if (referralCode) {
    form.referralCode = referralCode
  }
  fetchProCityArea()
  initTencentCaptcha().catch((error) => {
    console.error('Init Tencent captcha failed:', error)
  })
})

// 组件卸载时清理定时器
onUnmounted(() => {
  isUnmounted = true
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
  captcha = null
  document.getElementById('cap_iframe')?.replaceChildren()
})

// 校验函数
const validatePhoneLength = (value: string) => {
  return value.length <= 11
}

const validateCodeFormat = (value: string) => {
  return /^\d{1,6}$/.test(value)
}

const validateName = (value: string) => {
  return !value || value.length <= 50
}

const validateNoInvalidChars = (value: string) => {
  return !value || !INVALID_CHAR_REGEX.test(value)
}

const validateIdNumberFormat = (value: string) => {
  return /^\d{1,12}$/.test(value)
}

const validateEmailFormat = (value: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

const validatePostcode = (value: string) => {
  return !value || /^\d{5}$/.test(value)
}

const validateCity = () => {
  // 如果邮编有值，城市必须有值
  return !form.postcode || !!form.city
}

const validateAddress = (value: string) => {
  return !value || value.length <= 250
}

const resetVerifyToken = () => {
  form.verifyToken = ''
}

const onPhoneChange = () => {
  resetVerifyToken()
}

// 手机号处理：去掉 0 或 60 开头
const trimPhonePrefix = (phone: string): string => {
  let trimmed = phone
  if (trimmed.startsWith('60')) {
    trimmed = trimmed.slice(2)
  } else if (trimmed.startsWith('0')) {
    trimmed = trimmed.slice(1)
  }
  return trimmed
}

// 验证码变化处理
const onCodeChange = async (value: string) => {
  resetVerifyToken()

  if (value.length === 6 && form.phone) {
    try {
      const phoneNumber = trimPhonePrefix(form.phone)
      const res: any = await verifyPhoneCode({
        phoneNumber,
        securityCode: value
      })
      if (res) {
        form.verifyToken = res.token || res
      }
    } catch (error) {
      console.error('Verify code failed:', error)
    }
  }
}

// 邮编变化处理
const onPostcodeChange = (value: string) => {
  postcodeError.value = false
  form.province = ''
  form.city = ''
  form.area = ''

  if (value && value.length === 5) {
    const area = postcodeAreaMap.value.get(value)

    if (area) {
      form.province = area.province
      form.city = area.city
      form.area = area.area
    } else {
      postcodeError.value = true
      showFailToast(lang('postcode_not_found') as string)
    }
  }
}

// 隐私协议点击
const onPrivacyClick = () => {
  window.open('https://jtexpress.my/legal/privacy-policy3', '_blank')
}

const sendCodeWithCaptcha = async (captchaRes: TencentCaptchaResponse) => {
  if (isUnmounted) return

  try {
    const phoneNumber = trimPhonePrefix(form.phone)
    await sendRegisterCode({
      phoneNumber,
      img: captchaRes.randstr,
      imgToken: captchaRes.ticket
    })
    startCountdown()
  } catch (error) {
    console.error('Send code failed:', error)
  }
}

const tencentCaptchaCallback = (res: TencentCaptchaResponse) => {
  if (res.ret === 0 && res.ticket && res.randstr) {
    sendCodeWithCaptcha(res)
  }
}

// 发送验证码
const onSendCode = async () => {
  if (isCountingDown.value || isSendingCode.value) return

  if (!form.phone) {
    showFailToast(lang('phone_required') as string)
    return
  }

  isSendingCode.value = true
  try {
    const phone = trimPhonePrefix(form.phone)
    await checkPhoneRegister({ phone })

    if (!captcha) {
      await initTencentCaptcha()
    }
    captcha?.show()
  } catch (error) {
    console.error('Check phone failed:', error)
  } finally {
    isSendingCode.value = false
  }
}

const startCountdown = () => {
  countdown.value = COUNTDOWN_SECONDS
  countdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0 && countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }, 1000)
}

// 提交注册
const onSubmit = async () => {
  if (isSubmitting.value) return

  // 检查隐私协议
  if (!form.agreePrivacy) {
    showFailToast(lang('please_check_privacy') as string)
    return
  }

  // 如果邮编有值但城市为空，拦截
  if (form.postcode && !form.city) {
    showFailToast(lang('city_required') as string)
    return
  }

  if (!form.verifyToken) {
    showFailToast(lang('verification_code_error') as string)
    return
  }

  // 处理手机号前缀
  const phone = trimPhonePrefix(form.phone)

  const params = {
    appleUserId: '',
    referralCode: form.referralCode,
    email: form.email,
    phoneNumber: phone,
    areaCode: '60',
    postcode: form.postcode,
    token: form.verifyToken,
    password: form.password,
    idCard: form.idNumber,
    address: form.address,
    city: areaText.value,
    facebookUserId: '',
    googleUserId: '',
    username: form.name,
    securityCode: form.verificationCode,
    name: form.name
  }

  isSubmitting.value = true
  try {
    await submitRegister(params)
    router.push({ name: 'RegisterSuccess' })
  } catch (error) {
    console.error('Register failed:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="less" scoped>
.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.container {
  flex: 1;
  padding-top: calc(56px + v-bind(safeAreaTop));
  padding-bottom: calc(68px + v-bind(safeAreaBottom));
}

.section {
  margin: 0 8px 12px;
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;

  .section-title {
    font-size: 16px;
    font-weight: bold;
    color: #333333;
    line-height: 24px;
    padding: 12px 16px 8px;
    background-color: #ffffff;
  }
}

:deep(.van-cell-group--inset) {
  margin: 0;
  border-radius: 0;
  overflow: visible;
  background-color: transparent;
}

:deep(.van-cell.van-field) {
  width: auto;
  font-size: 16px;
  line-height: 24px;
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  margin: 8px 12px;
  padding: 10px 12px;

  input,
  textarea {
    font-size: 16px;
    line-height: 24px;
    color: #333333;

    &::placeholder {
      color: #cccccc;
    }
  }
}

:deep(.van-cell.van-field.readonly-field) {
  background-color: #f9f9f9;

  input {
    background-color: #f9f9f9;
    color: #666666;
  }
}

.send-code-btn {
  font-size: 14px;
  color: #e60012;
  cursor: pointer;

  &.disabled {
    color: #999999;
    cursor: not-allowed;
  }
}

.privacy-wrap {
  margin: 12px 12px 8px;
  display: flex;
  align-items: center;

  :deep(.van-checkbox__label) {
    font-size: 14px;
    line-height: 20px;
    color: #333333;
  }

  .privacy-text {
    font-size: 14px;
    color: #333333;
  }

  .privacy-link {
    color: #e60012;
    cursor: pointer;
  }
}

.download-wrap {
  margin: 0 12px 12px;
  text-align: center;
  font-size: 14px;
  line-height: 20px;

  .has-account {
    color: #999999;
  }

  .download-link {
    color: #e60012;
    text-decoration: none;
  }
}

.submit-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px calc(12px + v-bind(safeAreaBottom));
  background-color: #ffffff;
  z-index: 100;

  :deep(.van-button--primary) {
    background-color: #e60012;
    border-color: #e60012;
    font-size: 16px;
    line-height: 24px;
    height: 44px;
    border-radius: 8px;

    &.van-button--disabled {
      background-color: #f2f2f2;
      border-color: #f2f2f2;
      color: #adadad;
      opacity: 1;
    }
  }
}

.postcode-error {
  margin: -4px 12px 8px;
  font-size: 12px;
  color: #e60012;
  padding-left: 4px;
}

.lang-switch {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 8px;

  .lang-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
  }

  .lang-label {
    font-size: 14px;
    color: #171921;
  }
}

.lang-popup {
  left: auto !important;
  right: 8px !important;
  width: auto !important;
  min-width: 160px;
  border-radius: 8px;
  overflow: hidden;

  .lang-list {
    padding: 8px 0;
    background-color: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
  }

  .lang-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    cursor: pointer;

    &.active {
      background-color: #fff5f5;
    }

    .lang-item-icon {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      object-fit: cover;
    }

    .lang-item-label {
      font-size: 14px;
      color: #333333;
    }
  }
}
</style>
